import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import DebtCard from '../components/DebtCard';
import DebtModal from '../components/DebtModal';
import PaymentModal from '../components/PaymentModal';
import { getAllDebts } from '../services/debtService';
import type { Debt, DebtStatus } from '../types/debt';
import { Plus, Search, Building, SlidersHorizontal } from 'lucide-react';

const STATUS_FILTERS: { label: string; value: DebtStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'pending' },
  { label: 'Paying', value: 'partial' },
  { label: 'Paid Off', value: 'paid' },
];

const DebtListPage = () => {
  const [debts, setDebts] = useState<Debt[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DebtStatus | 'all'>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [editingDebt, setEditingDebt] = useState<Debt | null>(null);
  const [paymentDebt, setPaymentDebt] = useState<Debt | null>(null);

  const refresh = useCallback(async () => {
    const data = await getAllDebts();
    setDebts(data);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = debts
    .filter((d) => statusFilter === 'all' || d.status === statusFilter)
    .filter((d) => !search || d.creditorName.toLowerCase().includes(search.toLowerCase()) || d.description.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const total = filtered.filter(d => d.status !== 'paid').reduce((s, d) => s + (d.amount - d.amountPaid), 0);
  const currency = filtered[0]?.currency || 'AED';

  return (
    <div className="min-h-screen flex" style={{ background: '#0f0f13' }}>
      <Sidebar />
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="p-6 max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-8 fade-in-up">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-xl bg-indigo-500/15 flex items-center justify-center">
                  <Building className="h-4 w-4 text-indigo-400" />
                </div>
                <h1 className="text-2xl font-bold text-white">My Debts</h1>
              </div>
              <p className="text-sm text-gray-500">Track and manage your personal loans, credit cards, and payables</p>
              {filtered.length > 0 && (
                <p className="text-sm text-gray-400 mt-2">
                  <span className="font-semibold text-indigo-400">
                    {currency} {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                  {' '}outstanding across {filtered.filter(d => d.status !== 'paid').length} active {filtered.filter(d => d.status !== 'paid').length === 1 ? 'account' : 'accounts'}
                </p>
              )}
            </div>
            <button
              onClick={() => { setEditingDebt(null); setModalOpen(true); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-500/25 flex-shrink-0"
            >
              <Plus className="h-4 w-4" /> Add Debt
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6 fade-in-up delay-100">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                placeholder="Search by creditor or notes…"
                value={search} onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <div className="flex gap-1">
                {STATUS_FILTERS.map((f) => (
                  <button key={f.value} onClick={() => setStatusFilter(f.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === f.value ? 'bg-indigo-600 text-white' : 'text-gray-400 bg-white/5 hover:bg-white/10'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* List */}
          {filtered.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center fade-in-up delay-200">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-indigo-500/10">
                <Building className="h-7 w-7 text-indigo-400" />
              </div>
              <p className="text-white font-medium mb-1">{search ? 'No results found' : 'No debts yet'}</p>
              <p className="text-sm text-gray-500">{search ? 'Try a different search term.' : 'Click "Add Debt" to log a new payable account.'}</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((d) => (
                <DebtCard
                  key={d.id}
                  debt={d}
                  onEdit={(debt) => { setEditingDebt(debt); setModalOpen(true); }}
                  onPayment={(debt) => { setPaymentDebt(debt); setPaymentModal(true); }}
                  onDeleted={refresh}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <DebtModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSaved={refresh} editingDebt={editingDebt} />
      <PaymentModal isOpen={paymentModal} onClose={() => setPaymentModal(false)} onSaved={refresh} debt={paymentDebt} />
    </div>
  );
};

export default DebtListPage;
